/**
 * Replaces (or adds) a `key:value` term in an omnisearch DSL string, quoting
 * values that contain whitespace. Used by the vendor-dashboard tiles to drive
 * the manifests grid filter from chart clicks. tenantId / shippedDate are NOT
 * injected here — they come from the grid's defaultSearch (smart-merged).
 *
 * `key` is escaped before being interpolated into the strip regex so a future
 * caller passing a dotted form like `facility.name` doesn't turn `.` into a
 * wildcard. Inner `"` and `\` in `value` are backslash-escaped on the way out
 * and reversed by the strip regex so a vendor labelled `Foo "Inc"` round-trips
 * cleanly instead of corrupting the search on the next click.
 */
/**
 * Strips a `key:value` term from an omnisearch DSL string, leaving the rest
 * intact. Used by the vendor-dashboard tiles when a chart selection toggles
 * OFF, so the filter the toggle-on click added leaves the grid too.
 */
export function removeOmnisearchTerm(search: string, key: string): string {
  const escapedKey = key.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
  // Quoted segment matches a `"`-delimited value with backslash escapes, so the
  // strip pairs with the escape upsertOmnisearchTerm applies on the way in.
  const stripRegex = new RegExp(`(?:^|\\s)${escapedKey}:(?:"(?:\\\\.|[^"\\\\])*"|\\S+)`, "gi");
  return search.replace(stripRegex, " ").replace(/\s+/g, " ").trim();
}

export function upsertOmnisearchTerm(search: string, key: string, value: string): string {
  const cleaned = removeOmnisearchTerm(search, key);
  const needsQuotes = /[\s"\\]/.test(value);
  const serialized = needsQuotes ? `"${value.replace(/(["\\])/g, "\\$1")}"` : value;
  return `${cleaned} ${key}:${serialized}`.trim();
}
